import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { UpOutlined, DownOutlined } from "@ant-design/icons";
import Select from "react-select";
import { Table, Checkbox } from "antd";
import Swal from "sweetalert2";
import {
  updateRole,
  getModules,
  getRolePermissionDetailsById,
} from "../../../service/userRoleApiServices";
import permissionsData from "../../../permission.json";

const EditRole = ({ RoleId, handlereload }) => {
  const [modules, setModules] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [functions, setFunctionList] = useState([]);
  const [modulesList, setModulesList] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [filteredModules, setFilteredModules] = useState([]);
  const [data, setData] = useState([]);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [selectedSubFunctions, setSelectedSubFunctions] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getModules().then((value) => {
      setModulesList(value);
      const map = value.map((module) => ({
        label: module.label,
        value: module._id,
      }));
      setModules(map);
    });
  }, []);

  useEffect(() => {
    const fetchRoleData = async () => {
      if (!RoleId) return;

      setLoading(true);
      try {
        const roleData = await getRolePermissionDetailsById(RoleId);

        if (!roleData) {
          throw new Error("Role data not available");
        }

        setRoleName(roleData.roleName || "");
        
        const roleModules = roleData.modules?.map((module) => ({
          label: module.moduleId?.label || "Unknown Module",
          value: module.moduleId?._id || module.moduleId,
        })) || [];

        setSelectedModules(roleModules);

        const newFunctionList = [];
        const newFilteredModules = [];
        const newSelectedPermissions = [];
        const newSelectedFunctions = [];
        const newSelectedSubFunctions = [];

        roleData.modules?.forEach((module) => {
          const moduleId = module.moduleId?._id || module.moduleId;
          const moduleInfo = modulesList.find((m) => m._id === moduleId);
        
          if (moduleInfo) {
            newFilteredModules.push(moduleInfo);
        
            module.functions?.forEach((func) => {
              const functionId = func.functionId?._id || func.functionId;
              const functionInfo = moduleInfo.functions?.find(
                (f) => f._id === functionId
              );
        
              if (functionInfo) {
                newFunctionList.push({
                  module: moduleId,
                  function: {
                    ...functionInfo,
                    subFunctions: functionInfo.subFunctions || [],
                  },
                });

                // Check if function has any permissions
                const hasPermissions = func.permissions && Object.values(func.permissions).some(Boolean);
                if (hasPermissions) {
                  newSelectedFunctions.push({
                    functionId: functionId,
                    isEnabled: true,
                  });
                }

                // Process function permissions
                if (func.permissions) {
                  Object.entries(func.permissions).forEach(
                    ([permKey, isEnabled]) => {
                      if (isEnabled) {
                        newSelectedPermissions.push({
                          functionId: functionId,
                          permission: permKey,
                          moduleId: moduleId,
                          isChecked: true,
                        });
                      }
                    }
                  );
                }

                // Process sub-functions
                func.subFunctions?.forEach((subFunc) => {
                  const subFunctionId = subFunc.functionId?._id || subFunc.functionId;
                  const hasSubPermissions = subFunc.permissions && Object.values(subFunc.permissions).some(Boolean);
                  
                  if (hasSubPermissions) {
                    newSelectedSubFunctions.push({
                      subFunctionId: subFunctionId,
                      parentFunctionId: functionId,
                      isEnabled: true,
                    });
                  }

                  if (subFunc.permissions) {
                    Object.entries(subFunc.permissions).forEach(
                      ([permKey, isEnabled]) => {
                        if (isEnabled) {
                          newSelectedPermissions.push({
                            subFunctionId: subFunctionId,
                            parentFunctionId: functionId,
                            permission: permKey,
                            moduleId: moduleId,
                            isChecked: true,
                          });
                        }
                      }
                    );
                  }
                });
              }
            });
          }
        });

        setFunctionList(newFunctionList);
        setFilteredModules(newFilteredModules);
        setSelectedPermissions(newSelectedPermissions);
        setSelectedFunctions(newSelectedFunctions);
        setSelectedSubFunctions(newSelectedSubFunctions);
        setData(roleData.modules || []);
      } catch (error) {
        console.error("Error fetching role details:", error);
        Swal.fire({
          title: "Error",
          text: error.message || "Failed to load role data",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    if (modulesList.length > 0 && RoleId) {
      fetchRoleData();
    }
  }, [RoleId, modulesList]);

  const handleCheckboxChange = (functionId, permissionType, isChecked, moduleId) => {
    setSelectedPermissions(prev => {
      // Remove existing permission if it exists
      const filtered = prev.filter(
        item => !(item.functionId === functionId && 
                 item.permission === permissionType &&
                 !item.subFunctionId)
      );
      
      // Add new permission if checked
      if (isChecked) {
        filtered.push({
          functionId,
          permission: permissionType,
          moduleId,
          isChecked: true
        });
      }
      
      return filtered;
    });
  };

  const handleSubFunctionCheckboxChange = (
    subFunctionId,
    parentFunctionId,
    permissionType,
    isChecked,
    moduleId
  ) => {
    setSelectedPermissions(prev => {
      // Remove existing permission if it exists
      const filtered = prev.filter(
        item => !(item.subFunctionId === subFunctionId &&
                 item.parentFunctionId === parentFunctionId &&
                 item.permission === permissionType)
      );
      
      // Add new permission if checked
      if (isChecked) {
        filtered.push({
          subFunctionId,
          parentFunctionId,
          permission: permissionType,
          moduleId,
          isChecked: true
        });
      }
      
      return filtered;
    });
  };

  const handleEnableFunction = (functionId, isEnabled) => {
    setSelectedFunctions(prev => {
      const existing = prev.find(item => item.functionId === functionId);
      if (existing) {
        return prev.map(item => 
          item.functionId === functionId ? { ...item, isEnabled } : item
        );
      }
      return [...prev, { functionId, isEnabled }];
    });
  };

  const handleEnableSubFunction = (subFunctionId, parentFunctionId, isEnabled) => {
    setSelectedSubFunctions(prev => {
      const existing = prev.find(item => 
        item.subFunctionId === subFunctionId && 
        item.parentFunctionId === parentFunctionId
      );
      
      if (existing) {
        return prev.map(item => 
          (item.subFunctionId === subFunctionId && 
           item.parentFunctionId === parentFunctionId) ? { ...item, isEnabled } : item
        );
      }
      return [...prev, { subFunctionId, parentFunctionId, isEnabled }];
    });
  };

  const isFunctionEnabled = (functionId) => {
    return selectedFunctions.some(
      item => item.functionId === functionId && item.isEnabled
    );
  };

  const isSubFunctionEnabled = (subFunctionId, parentFunctionId) => {
    return selectedSubFunctions.some(
      item => item.subFunctionId === subFunctionId && 
             item.parentFunctionId === parentFunctionId && 
             item.isEnabled
    );
  };

  const isPermissionChecked = (functionId, permissionType) => {
    return selectedPermissions.some(
      item => item.functionId === functionId && 
             item.permission === permissionType &&
             !item.subFunctionId
    );
  };

  const isSubFunctionPermissionChecked = (subFunctionId, parentFunctionId, permissionType) => {
    return selectedPermissions.some(
      item => item.subFunctionId === subFunctionId &&
             item.parentFunctionId === parentFunctionId &&
             item.permission === permissionType
    );
  };

  const customStyles = {
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: "#ff7f00",
      borderRadius: "12px",
      color: "white",
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: "white",
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: "white",
      ":hover": {
        backgroundColor: "#ff7f00",
        color: "white",
      },
    }),
  };

  const handleChangeModule = (val) => {
    setFunctionList([]);
    setFilteredModules([]);
    setSelectedModules(val);

    val.forEach((a) => {
      const filtered = modulesList.find((value1) => value1._id === a.value);
      if (filtered) {
        setFilteredModules((prev) => [...prev, filtered]);
        filtered.functions.forEach((value) => {
          setFunctionList((prevModules) => [
            ...prevModules,
            { module: filtered._id, function: value },
          ]);
        });
      }
    });
  };

  const showSuccess = (msg, type, title) => {
    Swal.fire({
      title: title,
      text: msg,
      icon: type,
    });
  };

  const handleSaveChanges = async () => {
    if (!roleName || filteredModules.length === 0) {
      Swal.fire({
        icon: "warning",
        text: "Please fill in all the required fields to update."
      });
      return;
    }
  
    try {
      setLoading(true);
      
      // Create the update payload
      const updateData = {
        roleName: roleName,
        modules: filteredModules.map(module => ({
          moduleId: module._id,
          functions: functions
            .filter(func => func.module === module._id)
            .map(func => {
              // Function permissions
              const functionPermissions = {};
              permissionsData.permissions.forEach(perm => {
                functionPermissions[perm.key] = isPermissionChecked(
                  func.function._id, 
                  perm.key
                );
              });

              // Sub-functions with their permissions
              const subFunctions = func.function.subFunctions?.map(subFunc => {
                const subPermissions = {};
                permissionsData.permissions.forEach(perm => {
                  subPermissions[perm.key] = isSubFunctionPermissionChecked(
                    subFunc._id || subFunc.functionId,
                    func.function._id,
                    perm.key
                  );
                });
                return {
                  functionId: subFunc._id || subFunc.functionId,
                  permissions: subPermissions
                };
              }).filter(subFunc => 
                Object.values(subFunc.permissions).some(val => val === true)
              ) || [];
              
              return {
                functionId: func.function._id,
                permissions: functionPermissions,
                subFunctions: subFunctions
              };
              }).filter(func => 
                Object.values(func.permissions).some(val => val === true) || 
                (func.subFunctions && func.subFunctions.length > 0)
              )
        }))
      };
  
      const response = await updateRole(RoleId, updateData);
      
      if (response && response.role) {
        showSuccess(
          "Role updated successfully",
          "success",
          "Success"
        );
        
        if (handlereload) {
          await handlereload();
        }
  
        // Close modal
        const modal = document.getElementById('edit-units');
        if (modal) {
          modal.classList.remove('show');
          modal.style.display = 'none';
          document.body.classList.remove('modal-open');
          const backdrops = document.querySelectorAll('.modal-backdrop');
          backdrops.forEach(backdrop => backdrop.remove());
        }
      } else {
        throw new Error("Update failed - invalid response");
      }
    } catch (error) {
      console.error("Save Changes Error:", error);
      console.error("Save Changes Error:", data);

      Swal.fire({
        icon: "error",
        text: error.message || "Failed to update role"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setRoleName("");
    setData([]);
    setSelectedModules([]);
    setFunctionList([]);
    setSelectedPermissions([]);
    setFilteredModules([]);
    setSelectedFunctions([]);
    setSelectedSubFunctions([]);
  };

  const columns = [
    {
      title: "Function",
      dataIndex: "function",
      key: "function",
      render: (func) => <span>{func.label}</span>,
    },
    {
      title: "Enable",
      key: "enable",
      render: (text, record) => (
        <Checkbox
          checked={isFunctionEnabled(record.function._id)}
          onChange={(e) =>
            handleEnableFunction(record.function._id, e.target.checked)
          }
        />
      ),
    },
    ...permissionsData.permissions.map((perm) => ({
      title: perm.label,
      dataIndex: perm.key,
      key: perm.key,
      render: (text, record) => {
        if (!record.function.subFunctions || record.function.subFunctions.length === 0) {
          return (
            <Checkbox
              disabled={!isFunctionEnabled(record.function._id)}
              checked={isPermissionChecked(record.function._id, perm.key)}
              onChange={(e) =>
                handleCheckboxChange(
                  record.function._id,
                  perm.key,
                  e.target.checked,
                  record.module
                )
              }
            />
          );
        }
        return null;
      },
    })),
  ];

  return (
    <div>
      <div className="modal fade" id="edit-units">
        <div
          className="modal-dialog modal-lg modal-dialog-centered custom-modal-two"
          style={{ maxWidth: "80%" }}
        >
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-body custom-modal-body">
                  <div className="card">
                    <div className="card-header">
                      <h5 className="card-title">Edit Role</h5>
                    </div>
                    <div className="card-body">
                      {loading ? (
                        <div className="text-center">Loading...</div>
                      ) : (
                        <form action="#">
                          <div className="mb-3">
                            <label className="form-label">Role Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={roleName}
                              onChange={(e) => setRoleName(e.target.value)}
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Select Modules</label>
                            <Select
                              options={modules}
                              className="select2"
                              isMulti={true}
                              styles={customStyles}
                              value={selectedModules}
                              onChange={handleChangeModule}
                            />
                          </div>

                          <div className="mb-4">
                            <div className="table-responsive mt-4">
                              <Table
                                dataSource={functions}
                                columns={columns}
                                pagination={false}
                                expandable={{
                                  expandedRowRender: (record) => {
                                    if (record.function.subFunctions && record.function.subFunctions.length > 0) {
                                      return (
                                        <Table
                                          columns={[
                                            {
                                              title: "Sub-Function",
                                              dataIndex: "label",
                                              key: "label",
                                              render: (label, subRecord) => (
                                                <span>{subRecord.label || subRecord.functionId?.label}</span>
                                              ),
                                            },
                                            {
                                              title: "Enable",
                                              key: "enable",
                                              render: (text, subRecord) => (
                                                <Checkbox
                                                  checked={isSubFunctionEnabled(
                                                    subRecord._id || subRecord.functionId,
                                                    record.function._id
                                                  )}
                                                  onChange={(e) =>
                                                    handleEnableSubFunction(
                                                      subRecord._id || subRecord.functionId,
                                                      record.function._id,
                                                      e.target.checked
                                                    )
                                                  }
                                                />
                                              ),
                                            },
                                            ...permissionsData.permissions.map((perm) => ({
                                              title: perm.label,
                                              dataIndex: perm.key,
                                              key: perm.key,
                                              render: (text, subRecord) => (
                                                <Checkbox
                                                  disabled={!isSubFunctionEnabled(
                                                    subRecord._id || subRecord.functionId,
                                                    record.function._id
                                                  )}
                                                  checked={isSubFunctionPermissionChecked(
                                                    subRecord._id || subRecord.functionId,
                                                    record.function._id,
                                                    perm.key
                                                  )}
                                                  onChange={(e) =>
                                                    handleSubFunctionCheckboxChange(
                                                      subRecord._id || subRecord.functionId,
                                                      record.function._id,
                                                      perm.key,
                                                      e.target.checked,
                                                      record.module
                                                    )
                                                  }
                                                />
                                              ),
                                            })),
                                          ]}
                                          dataSource={record.function.subFunctions}
                                          pagination={false}
                                          rowKey={(subFunc) => subFunc._id || subFunc.functionId}
                                          bordered
                                        />
                                      );
                                    }
                                    return null;
                                  },
                                  expandIcon: ({
                                    expanded,
                                    onExpand,
                                    record,
                                  }) => {
                                    if (
                                      record.function.subFunctions &&
                                      record.function.subFunctions.length > 0
                                    ) {
                                      return (
                                        <span
                                          onClick={(e) => onExpand(record, e)}
                                          style={{ cursor: "pointer" }}
                                        >
                                          {expanded ? (
                                            <UpOutlined />
                                          ) : (
                                            <DownOutlined />
                                          )}
                                        </span>
                                      );
                                    }
                                    return null;
                                  },
                                }}
                                bordered
                                rowKey={(record) => record.function._id}
                              />
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                            className="mt-5"
                          >
                            <button
                              type="button"
                              className="btn btn-cancel"
                              data-bs-dismiss="modal"
                              onClick={handleReset}
                            >
                              Cancel
                            </button>
                            <div>
                              <button
                                type="button"
                                className="btn btn-reset me-2"
                                onClick={handleReset}
                              >
                                Reset
                              </button>

                              <button
                                type="button"
                                className="btn btn-submit"
                                onClick={handleSaveChanges}
                                disabled={loading}
                              >
                                {loading ? 'Saving...' : 'Save Changes'}
                              </button>
                            </div>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EditRole.propTypes = {
  RoleId: PropTypes.string.isRequired,
  handlereload: PropTypes.func,
};

export default EditRole;